/**
 * Station role vocabulary. MIRRORS core-cdk (cross-repo convention — keep in
 * sync by hand):
 *   packages/infra/lib/workosDesiredState.ts        (role catalog + permissions)
 *   packages/infra/lib/lambda/user-station-management/types.ts   (ROLE_RANK)
 * The backend enforces every rule mirrored here; these helpers only decide
 * what UI to offer.
 */
export interface StationRole {
  slug: string;
  label: string;
  /** Assignment rank: you may assign/change roles at or below your own. */
  rank: number;
  description: string;
  /**
   * Human-readable summary of the role's WorkOS permissions, for the role
   * reference UI. Mirrors the catalog's permission list in
   * workosDesiredState.ts — update together. bodycam:manage is deliberately
   * omitted: every role carries it, so it differentiates nothing.
   */
  capabilities: string[];
}

export const STATION_ROLES: StationRole[] = [
  {
    slug: "station_admin",
    label: "Station Admin",
    rank: 2,
    description: "Full station management, including users and reports",
    capabilities: [
      "Add, deactivate, and manage station users, roles, and badges",
      "Manage station settings, API keys, and the bodycam integration",
      "Create and edit their own reports; view and manage every station report",
      "View and manage every station incident",
      "View, download, and upload all station evidence",
      "Download evidence transcripts",
    ],
  },
  {
    slug: "user",
    label: "User",
    rank: 1,
    description: "Default role for station members",
    capabilities: [
      "Create, edit, and delete their own reports",
      "View and manage station incidents and attach files",
      "View, download, and upload their own evidence",
      "Download evidence transcripts",
    ],
  },
  {
    slug: "evidence-viewer",
    label: "Evidence Viewer",
    rank: 0,
    description: "Station-wide read access to evidence",
    capabilities: [
      "View and download every piece of evidence in the station",
      "Create, edit, and delete their own reports",
      "Upload evidence and attach files to incidents",
      "Download evidence transcripts",
    ],
  },
  {
    slug: "transcriber",
    label: "Transcriber",
    rank: 0,
    description: "Own-evidence editing plus transcript generation",
    capabilities: [
      "Generate and download evidence transcripts",
      "View, upload, and edit their own evidence",
      "Create, edit, and delete their own reports",
      "View and manage station incidents and attach files",
    ],
  },
  {
    slug: "station_transcriber",
    label: "Station Transcriber",
    rank: 0,
    description: "Station-wide evidence editing plus transcript generation",
    capabilities: [
      "Generate and download evidence transcripts",
      "View and edit every piece of evidence in the station",
      "Create, edit, and delete their own reports",
      "Upload evidence and attach files to incidents",
    ],
  },
];

/** Internal platform admin (KARDA org only) — never assignable to stations. */
export const KARDA_ADMIN_ROLE_SLUG = "org-karda-admin";
const KARDA_ADMIN_RANK = 3;

export function getStationRoleRank(slug?: string | null): number {
  if (!slug) return 0;
  if (slug === KARDA_ADMIN_ROLE_SLUG) return KARDA_ADMIN_RANK;
  return STATION_ROLES.find((role) => role.slug === slug)?.rank ?? 0;
}

/** Display label for a role slug; falls back to the raw slug for unknowns. */
export function getStationRoleLabel(slug?: string | null): string | null {
  if (!slug) return null;
  if (slug === KARDA_ADMIN_ROLE_SLUG) return "KARDA Admin";
  return STATION_ROLES.find((role) => role.slug === slug)?.label ?? slug;
}

/**
 * Roles the caller may assign: at or below their own rank (backend:
 * assertCallerCanAssignRole — so a station admin can grant station_admin).
 * Rank-0 callers get nothing: the backend fails closed on them rather than
 * letting rank 0 read as "may assign rank-0 catalog roles".
 */
export function assignableStationRoles(
  callerRoleSlug?: string | null,
): StationRole[] {
  const callerRank = getStationRoleRank(callerRoleSlug);
  if (callerRank === 0) return [];
  return STATION_ROLES.filter((role) => role.rank <= callerRank);
}

/**
 * Whether the caller may change this member's role at all: someone else's
 * account, with a current role at or below the caller's rank — so a station
 * admin can edit peer admins, but KARDA-held memberships are off-limits to
 * everyone through this UI (the backend 403s them outright). (The
 * invitation-only/no-id case is the caller's to exclude — those rows have no
 * membership to edit.)
 */
export function canChangeStationRole(
  callerRoleSlug: string | null | undefined,
  target: { email: string; role?: string | null },
  currentUserEmail: string | null | undefined,
): boolean {
  if (currentUserEmail && target.email === currentUserEmail) return false;
  if (assignableStationRoles(callerRoleSlug).length === 0) return false;
  // Fail closed on a missing or out-of-catalog target role: the backend's
  // assertCallerCanChangeRoleOf 403s these as role_change_target_unresolved,
  // so offering the item could only produce that 403. (Catalog rank-0 roles
  // like transcriber stay editable; treating unknowns as rank 0 would not.)
  if (!target.role) return false;
  // KARDA-held memberships are protected server-side no matter the caller.
  if (target.role === KARDA_ADMIN_ROLE_SLUG) return false;
  if (!STATION_ROLES.some((role) => role.slug === target.role)) return false;
  return getStationRoleRank(target.role) <= getStationRoleRank(callerRoleSlug);
}
